import React from 'react';

import { Location as HistLocation } from 'history';
import _ from 'lodash';
import history from 'providers/history';
import { parse, stringify } from 'query-string';
import FontAwesome from 'react-fontawesome';
import Router from 'react-router';

import BreadcrumbTrail from 'components/global/application/BreadcrumbTrail';
import Disclaimer from 'components/pages/inventory/Disclaimer';
import ResultsFooter from 'containers/pages/inventory/ResultsFooter';
import { categoryEncode } from 'contexts/link_util';
import Log from 'providers/log';
import InventoryFilter from './InventoryFilter';
import InventoryItem from './InventoryItem';
import InventoryNav from './InventoryNav';

// import { categoryOptions } from 'constants/filter_options';
import {
    CategoryMapping,
    GetInstrumentsOptionKeys,
    GetInstrumentsOptions,
    InventoryInstrument
} from 'types/drupal/InstrumentNode';

import './Inventory.sass';

import { Helmet } from 'react-helmet';

const staticPathNodes: { label: string; route: string; }[] = [
    {
        label: 'Home',
        route: '/'
    },
    {
        label: 'Instruments & Amps',
        route: '/inventory'
    }
];

interface OwnProps {
    match: Router.match<{}>;
    location: Location;
}

export interface StateProps {
    category_options: CategoryMapping[];
    isMobile: boolean;
    inventory_items: InventoryInstrument[];
    num_pages: number;
    get_options: Partial<GetInstrumentsOptions>;
    none_found: boolean;
    viewFilters: boolean;
}

export interface DispatchProps {
    loadInventory(get_options_param?: Partial<GetInstrumentsOptions>): void;
    toggleFilterView(value: boolean): void;
}

type CombinedProps = OwnProps & StateProps & DispatchProps;

class Inventory extends React.Component<CombinedProps, {}> {
    private unregisterHistoryListener: () => void;

    constructor(props: CombinedProps) {
        super(props);

        this.filterOptionsSet = this.filterOptionsSet.bind(this);
        this.updateCategory = this.updateCategory.bind(this);
        this.updateQueryString = this.updateQueryString.bind(this);
        this.updateData = this.updateData.bind(this);
    }

    /**
     * boolean
     * are there filters in the "location"?
     */
    public filterOptionsSet(): boolean {
        const { location } = this.props;
        const queryObj = parse(location.search);
        const queryKeys = Object.keys(queryObj);

        /**
         * make sure page and category don't get sucked into this check,
         * they are not specifically filters though they are in the query string
         *
         * Note: category is only used in mobile view
         */
        const filterKeys = queryKeys.filter(k => k.indexOf('page') === -1 && k !== 'category');
        return filterKeys.length > 0;
    }

    /**
     * Update the query url based on key value
     */
    public updateQueryString(key: string, value?: string) {
        const { location, match } = this.props;
        const queryUpdate = parse(location.search);

        // set to undefined to remove during stringify
        queryUpdate[key] = value;

        const newQueryString = stringify(queryUpdate);

        // update or remove query string
        history.push(`${match.url}${newQueryString.length > 0 ? `?${newQueryString}` : ''}`);
    }

    /**
     * update the category url by way of react-select
     * Note: this is only used in mobile view
     * This is used to replace the category navigation.
     */
    public updateCategory(value: string) {
        const { search } = this.props.location;
        if (value.length < 1) {
            history.push(`/inventory${search}`);
        } else {
            history.push(`/inventory/category/${value}${search}`);
        }
    }

    public componentDidMount() {
        this.updateData(this.props.location);
        this.unregisterHistoryListener = history.listen(location => this.updateData(location));
    }

    public componentWillUnmount() {
        this.unregisterHistoryListener();
    }

    public render() {
        return this.props.isMobile ? this.Mobile(this.props) : this.Desktop(this.props);
    }

    public componentWillReceiveProps(nextState: CombinedProps) {
        if (
            nextState.num_pages !== 0
            && nextState.get_options.hasOwnProperty('page')
            && nextState.num_pages < nextState.get_options.page
        ) this.updateQueryString('page', nextState.num_pages.toString());
    }

    /**
     * Update inventory data
     */
    private updateData(location: Location | HistLocation) {

        /**
         * only run if this is an inventory page
         * Note: Not 100% sure why this needs to be checked
         *
         * accepted: "/inventory"
         * rejected: "/inventory/"
         */
        if (location.pathname !== '/inventory' && location.pathname.indexOf('/inventory/category/') === -1) {
            return;
        }

        const {
            category_options,
            inventory_items,
            get_options
        } = this.props;

        const queryObj = parse(location.search);
        const queryKeys = Object.keys(queryObj);

        /**
         * validate query keys discarding invalid keyValuePairs
         * checked against GetInstrumentsOptionKeys
         */
        const passable_options: Partial<GetInstrumentsOptions> = {};
        queryKeys.forEach((k: string) => {
            if (GetInstrumentsOptionKeys.indexOf(k) !== -1) {
                const val = queryObj[k];
                passable_options[k] = val; // === null ? true : val;
            }
        });

        /**
         * Run if '/inventory/category/' exists
         * Add category to passable_options object
         * ONLY: if it exists within category_options
         */
        if (location.pathname.indexOf('/inventory/category/') !== -1) {
            const split = location.pathname.split('/');

            // discard /inventory bit (including empty split value before leading slash)
            split.splice(0, 2);

            // CATEGORY = 'category' but this is shorter
            const [
                CATEGORY,
                category_value
            ] = split;

            // format the cateogry
            const category_formatted = categoryEncode(category_value);

            // get the targeted category
            const target = category_options.find(v => category_formatted === categoryEncode(v.description));

            // add category to passable options
            if (target) {
                passable_options[CATEGORY] = target.tid.toString();
            } else {
                Log.error(`No category match found for url value of "${location.pathname}"`);
            }
        }

        /**
         * Only equal if items have been loaded with those options
         * and are still in state or on initial load
         */
        if (!_.isEqual(passable_options, get_options)) {
            this.props.loadInventory(passable_options);
            return;
        }

        // First time nav and url with no query string
        if (inventory_items.length < 1) {
            this.props.loadInventory();
            return;
        }
    }

    private addMetaDescriptionAndTitleTags(location: string) {
        let formattedLocation = location;
        // remove trailing slashes to simplify switch matching
        if (formattedLocation.substr(formattedLocation.length - 1) === '/') {
            formattedLocation = formattedLocation.substr(0, formattedLocation.length - 1);
        }

        let description = '';
        let title = '';

        switch (formattedLocation) {
            case '/inventory/category/banjos':
                description  = 'Shop vintage banjos at Gruhn Guitars.';
                title = 'Vintage Banjos at Gruhn Guitars | Serving Nashville since 1970';
                break;
            case '/inventory/category/martin-flattops':
                description  = 'Shop vintage Martin Guitars and explore Vintage Martin Guitars values at Gruhn Guitars.';
                title = 'Vintage Martin Guitars For Sale - Martin Flattops | Gruhn Guitars, Nashville, TN';
                break;
            case '/inventory/category/electric-guitars':
                description  = 'Vintage Electric Guitar Inventory | Gruhn Guitars, Nashville, TN - Since 1970';
                title = 'Browse our collection of vintage electric guitars for sale online and in-store, only at Gruhn Guitars.';
                break;
            // on purpose fall-through - /inventory is the more general / default case
            case '/inventory':
            default:
                description  = 'Shop our selection of vintage guitars at Gruhn Guitars, located in Nashville, Tennessee.';
                title = 'Vintage Guitars | Vintage Mandolins, Banjos, & Basses | Gruhn Guitars';
                break;
        }

        return (
            <Helmet>
                <meta name="description" content={description} />
                <meta name="title" content={title} />
                <title>{title}</title>
            </Helmet>
        );
    }

    private Desktop: React.StatelessComponent<OwnProps> = (props: CombinedProps) => {
        const { category_options, get_options, inventory_items, isMobile, location, none_found } = this.props;
        return(
            <div className="Inventory-container row">
               {this.addMetaDescriptionAndTitleTags(location.pathname)}
                <div className="offset-1 col-10">
                    <div className="row">
                        <div className="col-4">
                            <div className="Side-pane">
                                <InventoryNav />
                                <hr /> {/* TODO: Replace this with something better */}
                                <InventoryFilter
                                    expandFilter={this.filterOptionsSet()}
                                    isMobile={isMobile}
                                    toggleFilterView={this.props.toggleFilterView}
                                    updateCategory={this.updateCategory}
                                    updateQueryString={this.updateQueryString}
                                    viewFilters={this.props.viewFilters}
                                />
                            </div>
                        </div>
                        <div className="col-8 Results-area">
                            {none_found ?
                                (<div className="No-results-text" >No items found matching specified criteria. Try removing filters</div>)
                                : inventory_items.length < 1
                                    ? (<FontAwesome className="loading-spinner" name="spinner" size="3x" spin />)
                                    : (
                                        <div className="Guitars">
                                            <div className="Results-breadcrumb">
                                                <BreadcrumbTrail
                                                    pathNodes={get_options.category
                                                        ? [...staticPathNodes,
                                                            {
                                                                isActiveOverride: () => true,
                                                                label: category_options.find(v => v.tid.toString() === get_options.category).description,
                                                                route: location.pathname
                                                            }
                                                        ]
                                                        : staticPathNodes
                                                    }
                                                />
                                            </div>
                                            {get_options.category && <div className="ResultsTitle">{category_options.find(v => v.tid.toString() === get_options.category).description}</div>}
                                            {
                                                // Display items in rows of four
                                                inventory_items.map((intsr: InventoryInstrument) => {
                                                    return <div key={intsr.nid} className="col-3"><InventoryItem isMobile={isMobile} item={intsr} /></div>;
                                                }).reduce<JSX.Element[][]>((prev: JSX.Element[][], cur: JSX.Element, index: number) => {
                                                    if (index % 4 === 0) prev.push(Array());
                                                    prev[prev.length - 1].push(cur);
                                                    return prev;
                                                },                         []).map((subElms: JSX.Element[]) => {
                                                    return <div
                                                        className="row"
                                                        key={subElms.map((cur: JSX.Element) => {
                                                            return cur.key;
                                                        }).join()}
                                                    >
                                                    {subElms}
                                                    </div>;
                                                })
                                            }
                                            <hr />
                                            <ResultsFooter updateQueryString={this.updateQueryString} />
                                            <Disclaimer />
                                        </div>)
                            }
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    private Mobile: React.StatelessComponent<OwnProps> = (props: CombinedProps) => {
        const { category_options, inventory_items, isMobile, get_options, none_found } = this.props;
        return (
            <div className="row Inventory-container-mobile">
                {this.addMetaDescriptionAndTitleTags(location.pathname)}
                <div className="col-10 offset-1 Breadcrumb">
                    <BreadcrumbTrail
                        pathNodes={get_options.category
                            ? [...staticPathNodes,
                                {
                                    isActiveOverride: () => true,
                                    label: category_options.find(v => v.tid.toString() === get_options.category).description,
                                    route: location.pathname
                                }
                            ]
                            : staticPathNodes
                        }
                    />
                </div>
                <div className="col-10 offset-1">
                    <InventoryFilter
                        isMobile={isMobile}
                        toggleFilterView={this.props.toggleFilterView}
                        updateQueryString={this.updateQueryString}
                        updateCategory={this.updateCategory}
                        viewFilters={this.props.viewFilters}
                    />
                </div>
                <div className="col-10 offset-1 ResultsTitle">&nbsp;</div>
                <div className="col-10 offset-1 Guitars">
                {none_found ?
                    (<div className="No-results-text" >No items found matching specified criteria. Try removing filters</div>)
                    : inventory_items.length < 1
                        ? (<FontAwesome className="loading-spinner" name="spinner" size="3x" spin />)
                        : inventory_items.map((gt: InventoryInstrument) => {
                            // Display items in rows of two
                            return <div key={gt.nid} className="col-6"><InventoryItem isMobile={isMobile} item={gt} /></div>;
                        }).reduce<JSX.Element[][]>(
                            (prev: JSX.Element[][], cur: JSX.Element, index: number) => {
                                if (index % 2 === 0) prev.push(Array());
                                prev[prev.length - 1].push(cur);
                                return prev;
                            },
                            []  // <- Initial value to reduce into
                        ).map((subElms: JSX.Element[]) => {
                            return (
                            <div className="row" key={subElms.map(cur => cur.key).join()} >
                            {subElms}
                            </div>);
                        })
                }
                </div>
                <div className="col-12 text-center">
                    {inventory_items.length > 0 && <ResultsFooter updateQueryString={this.updateQueryString} />}
                </div>
                <div className="col-12">
                    <Disclaimer />
                </div>
            </div>
        );
    }
}

export default Inventory;



// WEBPACK FOOTER //
// ./src/components/pages/inventory/Inventory.tsx